/**
 * ============================================================================
 * useDocHeadings — Extrai headings (H1-H4) de um documento Tiptap (JSON),
 *                  gerando IDs estáveis a partir do texto (slug + hash curto).
 *                  Usado pelo painel Outline no editor e no reader.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useMemo } from "react";
import { computeHeadingId, slugifyHeading } from "@/lib/kb/headingId";

export interface DocHeading {
  id: string;      // id estável para âncora no DOM
  level: number;   // 1..6
  text: string;
}

// Percorre recursivamente o JSON do Tiptap acumulando texto dos filhos.
function collectText(node: any): string {
  if (!node) return "";
  if (typeof node.text === "string") return node.text;
  if (Array.isArray(node.content)) {
    return node.content.map(collectText).join("");
  }
  return "";
}

function walkHeadings(node: any, out: DocHeading[], seen: Map<string, number>) {
  if (!node || typeof node !== "object") return;
  if (node.type === "heading") {
    const level = Number(node.attrs?.level ?? 1);
    const text = collectText(node).trim();
    if (text) {
      // Conta ocorrências por slug (não por texto) para bater com o plugin PM,
      // que também deduplica por slug ao percorrer o doc na mesma ordem.
      const base = slugifyHeading(text) || "sec";
      const count = (seen.get(base) ?? 0) + 1;
      seen.set(base, count);
      out.push({ id: computeHeadingId(text, count), level, text });
    }
  }
  if (Array.isArray(node.content)) {
    for (const child of node.content) walkHeadings(child, out, seen);
  }
}

export function extractHeadings(doc: unknown): DocHeading[] {
  if (!doc || typeof doc !== "object") return [];
  const out: DocHeading[] = [];
  walkHeadings(doc, out, new Map());
  return out;
}

// Hook: memoiza extração por identidade profunda (JSON.stringify curto).
export function useDocHeadings(doc: unknown): DocHeading[] {
  return useMemo(() => extractHeadings(doc), [JSON.stringify(doc ?? null)]);
}
